'use client';

import React, {
	useState,
	createContext,
	useContext,
	PropsWithChildren,
	useRef,
	useEffect,
} from 'react';
import { Button, Modal, Link } from '@fshr/components';
import { useTranslations } from '@fshr/context/i18n-context';
import { usePathname, useRouter } from 'next/navigation';

interface ConfirmationModalProps {
	href: string;
	title?: string;
	description?: string;
	cancelLabel?: string;
	continueLabel?: string;
	modalContent?: React.JSX.Element;
	onCancel?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	onContinue?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

interface ConfirmationModalContextType {
	showModal: (config?: ConfirmationModalProps) => void;
	setShouldShowModal: (shouldShow: boolean) => void;
	closeModal: () => void;
}

const ConfirmationModalContext = createContext<ConfirmationModalContextType | undefined>(undefined);

function ModalContent({
	href,
	title,
	description,
	cancelLabel,
	continueLabel,
	modalContent,
	onCancel,
	onContinue,
	closeModal,
}: ConfirmationModalProps & { closeModal: () => void }) {
	const t = useTranslations();
	const router = useRouter();
	return (
		<div className="h-full w-full flex flex-col justify-start md:justify-center">
			<div className="w-full flex flex-col items-center max-w-[690px] spacing-stack-l">
				{modalContent ? (
					modalContent
				) : (
					<div className="flex flex-col items-center spacing-stack-l">
						<h2 className="text-h2 text-center uppercase">{title || t('confirmation')}</h2>
						<div className="text-body2 text-center flex flex-col">
							{description ? (
								<span>{description}</span>
							) : (
								<>
									<span>{t('Any changes you made on this form will not be saved.')}</span>
									<span>{t("Click 'Continue' to return to the form, or 'Cancel' to exit.")}</span>
								</>
							)}
						</div>
					</div>
				)}
				<div className="flex flex-row gap-150">
					<Link
						intent="outline"
						size="medium"
						href={href}
						onClick={e => {
							onCancel?.(e);
							e.preventDefault();
							closeModal();
							router.push(href);
						}}
					>
						{cancelLabel || t('Cancel')}
					</Link>
					<Button
						intent="filled"
						size="medium"
						onClick={e => {
							onContinue?.(e);
							e.preventDefault();
							closeModal();
						}}
					>
						{continueLabel || t('Continue')}
					</Button>
				</div>
			</div>
		</div>
	);
}

export default function ConfirmationModalProvider({ children }: PropsWithChildren) {
	const t = useTranslations();
	const router = useRouter();
	const pathname = usePathname();
	const modalConfig = useRef<ConfirmationModalProps>(null);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [shouldShowModal, setShouldShowModal] = useState(false);

	const showModal = (config?: ConfirmationModalProps) => {
		modalConfig.current = config || null;
		if (shouldShowModal) {
			setShowConfirmationModal(true);
		} else if (modalConfig.current?.href) {
			router.push(modalConfig.current.href);
		}
	};

	const closeModal = () => {
		setShowConfirmationModal(false);
	};

	useEffect(() => {
		setShowConfirmationModal(false);
		setShouldShowModal(false);
	}, [pathname]);

	return (
		<ConfirmationModalContext.Provider
			value={{
				showModal,
				setShouldShowModal,
				closeModal,
			}}
		>
			{children}
			{modalConfig.current && showConfirmationModal && (
				<div className="@container/layout">
					<Modal close={closeModal}>
						<ModalContent
							closeModal={closeModal}
							{...modalConfig.current}
							onCancel={
								modalConfig.current?.onCancel
									? modalConfig.current?.onCancel
									: () => setShouldShowModal(false)
							}
						/>
					</Modal>
				</div>
			)}
		</ConfirmationModalContext.Provider>
	);
}

const useConfirmationModal = () => {
	const context = useContext(ConfirmationModalContext);
	if (!context) {
		throw new Error('useConfirmationModal must be used within a ConfirmationModalProvider');
	}
	return context;
};

export { ConfirmationModalProvider, useConfirmationModal };
